import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Box,
  Button,
  createTheme,
  Dialog,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  styled,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import { DataGrid, GridFooter, GridFooterContainer, GridPreferencePanelsValue, useGridApiContext } from "@mui/x-data-grid";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import {
  useTriggerOrgUserSyncMutation,
  useUpdateUserOrgStatusBatchMutation,
} from "state/rtkQuery/user/userMutations";
import {
  useGetOrgUserSyncStatusQuery,
  useGetUsersByOrgAndStatusQuery,
} from "state/rtkQuery/user/userQueries";
import { userServiceApiSlice } from "state/services/userServiceApiSlice";

import LoadingButton from "components/LoadingButton/LoadingButton";
import { useCurrentUserRoleNames, usePermission } from "components/RequirePermission/useAccessPermissions";
import { useOrganizationCapabilities, useCopilotBudgetCapabilities } from "components/RequirePermission/useCapabilities";
import { useEffectiveOrgRole } from "components/RequirePermission/useEffectiveOrgRole";
import { isCrossOrgAdminRole } from "components/RequirePermission/RequireEcrOpsRole";

import BulkChangeBudgetModal from "../JoinedTab/BulkChangeBudgetModal";

import LargeDataPagination from "./LargeDataPagination/LargeDataPagination";
import SyncUsersConfirmationModal from "./SyncUsersConfirmationModal/SyncUsersConfirmationModal";
import ReusableMultiSelectModal from "./ReusableMultiSelectModal";
import PageSizeSelect, { DEFAULT_PAGE_SIZE } from "components/shared/ExpandableGrid/PageSizeSelect";

// The community DataGrid has no apiRef prop, so this renders nothing inside the grid and hands its api out to the page
// (the Toolbar slot is the one place that gets props, and an empty one takes no room).
const GridApiCapture = ({ holder }) => {
  holder.current = useGridApiContext();
  return null;
};

const ReusableDataGrid = ({
  columns,
  rows,
  orgName,
  viewType,
  setCurrentPage,
  currentPage,
  totalRecords,
  isLoading,
  setQuery,
  query,
  checkboxSelection,
  tabDescription,
  syncStatus,
  setSyncStatus,
  refetchRequested,
  multiUserAction,
  recordsPerPage = DEFAULT_PAGE_SIZE,
  onRecordsPerPageChange,
  enableHorizontalScroll = false,
  tableMinWidth,
  customSearchControls = null,
  customSearchInput = null,
  filterChips = null,
}) => {
  // Show / hide columns: the gear sits in the same row as Filters and Expand (no toolbar row of its own above the grid),
  // and opens the grid's own columns panel under itself.
  const gridApiRef = useRef(null);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const toggleColumns = () => {
    const api = gridApiRef.current?.current;
    if (!api) return;
    const panel = api.state?.preferencePanel;
    if (panel?.open && panel.openedPanelValue === GridPreferencePanelsValue.columns) api.hidePreferences();
    else api.showPreferences(GridPreferencePanelsValue.columns);
  };
  const columnsButton = (
    <Tooltip title="Show / hide columns">
      <IconButton ref={setColumnsAnchor} size="small" aria-label="Show or hide columns" onClick={toggleColumns} sx={{ color: "#555" }}>
        <SettingsOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
  const [queryData, setQueryData] = useState([]); // Store all users across pages
  const [isDataProcessingloading, setIsDataProcessingLoading] = useState(true);
  const [isSyncInitiatedByUser, setIsSyncInitiatedByUser] = useState(false);
  const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
  const [showBulkChangeBudgetModal, setShowBulkChangeBudgetModal] = useState(false);
  const [isSuccessMessageVisible, setIsSuccessMessageVisible] = useState(false);
  const [isSyncInProgress, setIsSyncInProgress] = useState(false);
  const [hasSyncRunStarted, setHasSyncRunStarted] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const userEmail = useSelector((state) => state.auth.userAccount?.username);
  const { data: platformData } = useGetPlatformsQuery();
  // Real, always-fresh RBAC data (GET /user/organizations) instead of the old
  // state.auth.permissions — plus the same global view-capability override
  // used for the outer admin/:orgId gate (RequireOrgRole), since Sync is an
  // Owner-or-Approver-level action, not Owner-only. Leadership and
  // OperationsUser are both org-scoped (isLeadership/isOperationsUser), not
  // part of the global override — see isCrossOrgAdminRole.
  const { isOwner: isOrgOwner, isApprover: isOrgApprover, isLeadership: isOrgLeadership, isOperationsUser: isOrgOperationsUser } = useEffectiveOrgRole(orgId);
  const hasGlobalViewAccess = usePermission("organization:read");
  const roleNames = useCurrentUserRoleNames();
  const isUserAuthorized =
    isOrgOwner ||
    isOrgApprover ||
    isOrgLeadership ||
    isOrgOperationsUser ||
    (hasGlobalViewAccess && isCrossOrgAdminRole(roleNames));
  // Sync writes/updates membership records (not just reads them), so the
  // button itself is only ENABLED with organization:id:update — it stays
  // visible to anyone with org-scope (isUserAuthorized above), just inert.
  // useOrganizationCapabilities/useCopilotBudgetCapabilities are already
  // scoped to THIS org's role (see useEffectiveOrgPermission): each checks
  // the specific role useEffectiveOrgRole(orgId) reports for this org
  // against THAT role's own permissions, not the user's global cross-org
  // union, so a role held on a DIFFERENT org can't leak in here.
  const { canUpdateOrganizationById } = useOrganizationCapabilities(orgId);
  // Change Budget directly overrides a user's AI Credit budget — a separate
  // capability from removing users (user:id:update, which already gates
  // whether checkboxSelection/globalSelectedRows can be populated at all).
  // Requires both create and id:update: the endpoint upserts a ULB row
  // (create if the user has none yet, update if they already do), and the
  // frontend can't know which in advance for a bulk selection.
  const { canCreateBudget, canUpdateBudget } = useCopilotBudgetCapabilities(orgId);
  const canChangeBudget = canCreateBudget && canUpdateBudget;

  // RTK Query for fetching sync task status
  const {
    data: orgUserSyncStatusData,
    isLoading: isOrgUserSyncStatusLoading,
    refetch: refetchOrgUserSyncStatus,
  } = useGetOrgUserSyncStatusQuery(
    { orgId },
    {
      skip: !orgId,
      pollingInterval: isSyncInProgress ? 15000 : 0,
    }
  );

  // Theme for tooltip
  const welcomeToolTipTheme = createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "0.9rem", // Adjust this value to your needs
            padding: "8px 12px",
          },
        },
      },
    },
  });

  //Functions to Handle Opening and Closing of Sync Modal
  const openSyncModal = () => {
    setIsSyncModalOpen(true);
  };

  const closeSyncModal = () => {
    setIsSyncModalOpen(false);
  };

  const syncTaskStatus = orgUserSyncStatusData?.status;
  const isSyncStatusInProgress = syncTaskStatus === "in_progress";

  // Trigger Sync Mutation
  const [
    triggerOrgUserSync,
    {
      isLoading: isTriggerOrgUserSyncLoading,
    },
  ] = useTriggerOrgUserSyncMutation();

  // Handle the Sync users button click
  const handleSyncClick = () => {
    setIsSyncInitiatedByUser(true);
    setHasSyncRunStarted(false);
    setIsSyncInProgress(true);
    closeSyncModal();
    triggerOrgUserSync({ orgId })
      .unwrap()
      .then((response) => {
        const triggerStatus = String(response?.status || "").toLowerCase();
        const startedStatuses = [
          "accepted",
          "queued",
          "starting",
          "in_progress",
          "already_running",
          "complete",
        ];

        if (startedStatuses.includes(triggerStatus)) {
          setHasSyncRunStarted(true);
        }

        refetchOrgUserSyncStatus();
      })
      .catch((error) => {
        setIsSyncInProgress(false);
        setIsSyncInitiatedByUser(false);
        setHasSyncRunStarted(false);
      });
  };

  // Determine Button State
  const isButtonDisabled = useMemo(() => {
    if (isOrgUserSyncStatusLoading || isTriggerOrgUserSyncLoading || isSyncInProgress) {
      return true;
    }

    return isSyncStatusInProgress;
  }, [
    isOrgUserSyncStatusLoading,
    isTriggerOrgUserSyncLoading,
    isSyncInProgress,
    isSyncStatusInProgress,
  ]);

  // Key for re-rendering the datagrid
  const dataGridKey = JSON.stringify(rows);

  // Custom locale text for datagrid empty rows
  const customLocaleText = {
    noRowsLabel: "No Users Found",
    // The selection bar above the column titles shows the count (across every page), so the footer does not repeat it.
    footerRowSelected: () => "",
  };

  const [selectedRows, setSelectedRows] = useState([]);
  const [globalSelectedRows, setGlobalSelectedRows] = useState(new Set());

  // Sets selected rows to the selected rows in the datagrid
  const handleSelectionModelChange = (selection) => {
    setSelectedRows(selection);

    // Update global selection - simple approach
    const newGlobalSelection = new Set(globalSelectedRows);

    // Remove deselected items from current page
    currentRecords.forEach(row => {
      if (!selection.includes(row.id)) {
        newGlobalSelection.delete(row.id);
      }
    });

    // Add newly selected items
    selection.forEach(id => {
      newGlobalSelection.add(id);
    });

    setGlobalSelectedRows(newGlobalSelection);
  };

  // Search TextField styles
  const textStyles = styled({
    root: {
      "& .Mui-focused": {
        color: "#008080",
        outline: "1px solid  #008080",
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontSize: "14px",
      },
    },
  });

  const [currentRecords, setCurrentRecords] = useState([]);

  // Monitor sync task status and refresh all org-history tabs when a user-triggered sync completes.
  useEffect(() => {
    if (syncTaskStatus === "in_progress") {
      setIsSyncInProgress(true);
      if (isSyncInitiatedByUser) {
        setHasSyncRunStarted(true);
      }
      return;
    }

    if (syncTaskStatus === "error") {
      setIsSyncInProgress(false);
      if (isSyncInitiatedByUser) {
        setIsSyncInitiatedByUser(false);
        setHasSyncRunStarted(false);
      }
      return;
    }

    if (syncTaskStatus === "never_run") {
      // Idle state before any successful run is observed.
      if (!isSyncInitiatedByUser || !hasSyncRunStarted) {
        setIsSyncInProgress(false);
        if (isSyncInitiatedByUser) {
          setIsSyncInitiatedByUser(false);
          setHasSyncRunStarted(false);
        }
      }
      return;
    }

    if (syncTaskStatus === "complete") {
      setIsSyncInProgress(false);

      if (isSyncInitiatedByUser && hasSyncRunStarted) {
        dispatch(
          userServiceApiSlice.util.invalidateTags([
            { type: "User", id: "OrgRequests-Requested" },
            { type: "User", id: "OrgRequests-Joined" },
            { type: "User", id: "OrgRequests-Rejected" },
            { type: "User", id: "OrgRequests-Remove-Requested" },
            { type: "User", id: "OrgRequests-Removed" },
          ])
        );
        setIsSuccessMessageVisible(true);
        setIsSyncInitiatedByUser(false);
        setHasSyncRunStarted(false);
      }
    }
  }, [syncTaskStatus, isSyncInitiatedByUser, hasSyncRunStarted, dispatch]);

  useEffect(() => {
    if (rows) {
      // Use current page rows for display
      setCurrentRecords(rows);

      // Update selectedRows to reflect global selection for current page
      const currentPageIds = rows.map(row => row.id);
      const currentPageSelection = currentPageIds.filter(id => globalSelectedRows.has(id));
      setSelectedRows(currentPageSelection);
    }
  }, [currentPage, recordsPerPage, rows, globalSelectedRows]);

  const calcPages = useMemo(() => {
    return Math.ceil(totalRecords / recordsPerPage);
  }, [totalRecords, recordsPerPage]);

  const handleSearchChange = (event) => {
    setCurrentPage(1);
    setQuery(event.target.value);
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
    } else {
      setShowMultiSelectModal(false);
      setSelectedRows([]);
      setGlobalSelectedRows(new Set());
    }
  };

  const openModal = () => {
    setShowMultiSelectModal(true);
  };

  const openBulkChangeBudgetModal = () => {
    setShowBulkChangeBudgetModal(true);
  };

  const closeBulkChangeBudgetModal = () => {
    setShowBulkChangeBudgetModal(false);
    setSelectedRows([]);
    setGlobalSelectedRows(new Set());
  };

  // Batch update user org status for multi-select functionality
  const [
    batchUpdateUserOrgStatus,
    { data: batchUpdateData, isLoading: isBatchUpdateUserOrgStatusLoading },
  ] = useUpdateUserOrgStatusBatchMutation();

  const action = (platformId, orgId, userEmail) => {
    return (rejectReason, otherReason, rowData) => {
      batchUpdateUserOrgStatus({
        platformId: platformId,
        orgId: orgId,
        userEmail: userEmail,
        rejectReason: rejectReason,
        otherReason: otherReason,
        rowData: rowData,
      });
    };
  };

  useEffect(() => {
    if (isSuccessMessageVisible) {
      const timer = setTimeout(() => {
        setIsSuccessMessageVisible(false);
      }, 7000); // Auto close after 5 seconds

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isSuccessMessageVisible]);

  useEffect(() => {
    if (rows) {
      // Accumulate users from all visited pages into queryData, so
      // cross-page bulk selection (and the budget modal's selectedUsers,
      // built from this) keeps working after paginating. A row already
      // present must still be REPLACED with the incoming copy, not skipped —
      // otherwise a refetch that updates an existing row (e.g. a budget
      // change invalidating the list) never reaches queryData, and anything
      // reading from it (like the Change Budget modal reopened on the same
      // selection) keeps showing the stale pre-update value.
      setQueryData(prevData => {
        const rowIds = new Set(rows.map(user => user.id));
        const otherPageUsers = prevData.filter(user => !rowIds.has(user.id));
        return [...otherPageUsers, ...rows];
      });
      setIsDataProcessingLoading(false);
    }
  }, [rows]);

  // Filters and search: on the page next to Expand, and again at the top of the expanded window.
  const searchControls = (
    <>
      {customSearchControls}
      {/* Dynamic Search/Filter Input or Default Search */}
      {customSearchInput || (
        <TextField
          id="outlined-search"
          placeholder="Search Users By Email"
          width="100%"
          type="search"
          className={textStyles.root}
          variant="outlined"
          InputProps={{
            style: {
              height: "31px",
            },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          {...(query !== undefined ? { value: query } : {})}
          onChange={handleSearchChange}
        />
      )}
    </>
  );

  // The grid itself, at a given height: 600px on the page, the full window height in full screen.
  const renderGrid = (height) => (
      <div
        style={{
          height,
          width: "100%",
          overflowX: enableHorizontalScroll ? "auto" : "hidden",
        }}
      >
        <DataGrid
          sx={{
            minWidth: tableMinWidth || "100%",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "13px !important",
              fontWeight: "normal !important",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#f6f6f6",
              maxHeight: "45px",
              fontSize: "12px !important",
            },
            "& .MuiDataGrid-row": {
              minHeight: "60px !important",
              boxShadow: "none !important",
              borderBottom: "1px solid #d4d4d4",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "14px !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflowX: enableHorizontalScroll ? "auto" : "hidden",
            },
          }}
          disableColumnMenu
          disableColumnFilter
          componentsProps={{ panel: { anchorEl: columnsAnchor, placement: "bottom-end" }, toolbar: { holder: gridApiRef } }}
          components={{ Footer: CustomFooter, Toolbar: GridApiCapture }}
          key={dataGridKey}
          getRowId={(row) => row.id}
          rows={currentRecords}
          columns={columns}
          disableVirtualization
          disableSelectionOnClick
          checkboxSelection={checkboxSelection}
          localeText={customLocaleText}
          onSelectionModelChange={handleSelectionModelChange}
          selectionModel={selectedRows}
          isRowSelectable={(params) =>
            params.row.status === "joined" ||
            params.row.status === "rejected" ||
            params.row.status === "removed"
          }
          loadingOverlayClassName="custom-loading-indicator"
          loading={
            isLoading ||
            isDataProcessingloading ||
            isBatchUpdateUserOrgStatusLoading
          }
        />
      </div>
  );

  // Bulk actions for the ticked rows, in one Actions menu above the grid (always in view, however few rows a search
  // leaves). The selection is kept across pages, so say so when some of it is on another page.
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Nothing to open while the list is empty (not while it is still loading).
  const noRows = !isLoading && (rows?.length ?? 0) === 0;
  const closeFullscreen = () => {
    setIsFullscreen(false);
    if (onRecordsPerPageChange && recordsPerPage !== DEFAULT_PAGE_SIZE) onRecordsPerPageChange(DEFAULT_PAGE_SIZE);
  };
  const selectedCount = globalSelectedRows.size;
  const selectionBar =
    selectedCount > 0 ? (
      <Box data-testid="selection-actions" sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5 }}>
        <Button
          variant="contained"
          size="small"
          aria-haspopup="menu"
          aria-expanded={Boolean(actionsAnchor)}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(event) => setActionsAnchor(event.currentTarget)}
          sx={{ textTransform: "uppercase", fontSize: "12px", backgroundColor: "#86BC25", color: "#fff", borderRadius: "6px", boxShadow: "none", "&:hover": { backgroundColor: "#76a821", boxShadow: "none" } }}
        >
          Actions ({selectedCount})
        </Button>
        <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
          {viewType === "Joined History" && canChangeBudget && (
            <MenuItem
              onClick={() => {
                setActionsAnchor(null);
                openBulkChangeBudgetModal();
              }}
            >
              Change Budget
            </MenuItem>
          )}
          <MenuItem
            sx={{ color: "#d32f2f" }}
            onClick={() => {
              setActionsAnchor(null);
              openModal();
            }}
          >
            {viewType === "Joined History" ? "Revoke All" : "Reset All"}
          </MenuItem>
        </Menu>
        <Typography sx={{ fontSize: "13px", color: "#666" }}>
          {selectedCount} selected{selectedCount > selectedRows.length ? " across pages" : ""}
        </Typography>
      </Box>
    ) : null;

  const CustomFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooterContainer sx={{ justifyContent: "flex-start" }}>
          <GridFooter
            sx={{
              border: "none",
              display: "flex",
              justifyContent: "space-between",
            }}
          />
        </GridFooterContainer>
        <GridFooterContainer>
          <LargeDataPagination
            nPages={calcPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </GridFooterContainer>
      </GridFooterContainer>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: "3rem",
      }}
    >
      {showMultiSelectModal && (
        <ReusableMultiSelectModal
          show={showMultiSelectModal}
          close={handleClose}
          selectedRows={Array.from(globalSelectedRows)}
          allRows={queryData}
          action={(rejectReason, otherReason) =>
            multiUserAction(Array.from(globalSelectedRows), rejectReason, otherReason)
          }
        />
      )}
      {showBulkChangeBudgetModal && (
        <BulkChangeBudgetModal
          show={showBulkChangeBudgetModal}
          close={closeBulkChangeBudgetModal}
          selectedUsers={Array.from(globalSelectedRows)
            .map((id) => queryData.find((row) => row.id === id))
            .filter(Boolean)}
          platformId={platformData?._id}
          approvedBy={userEmail}
          onRemoveUser={(userId) => {
            const updated = new Set(globalSelectedRows);
            updated.delete(userId);
            setGlobalSelectedRows(updated);
          }}
        />
      )}
      <SyncUsersConfirmationModal
        show={isSyncModalOpen}
        close={closeSyncModal}
        handleConfirmChange={handleSyncClick}
        OrgName={orgName}
      />
      <div
        style={{
          width: "100%",
          padding: "1rem",
          paddingTop: "0",
        }}
      >
        <div
          style={{
            fontWeight: "600",
            fontSize: "18px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              fontWeight: "600",
              fontSize: "18px",
              display: "flex",
              flexDirection: "column",
              width: "60%",
              marginBottom: "0.5rem",
            }}
          >
            {orgName || ""}
            <Typography variant="subtitle1">Org History</Typography>
          </div>
          {/* tabDescription.includes("Users in Requested") &&  */}

          {/* Sync Button - Show only for Requested */}
          <ThemeProvider theme={welcomeToolTipTheme}>
            {isUserAuthorized &&
              (
                <Tooltip
                  placement="right"
                  title={
                    canUpdateOrganizationById ? (
                      <div>
                        {
                          "This action syncs the user's membership status in our database with the latest available information from GitHub."
                        }
                        <br />
                        {
                          "If users are stuck in Requested state for more than 2 hours, please raise an ECR support request."
                        }
                      </div>
                    ) : (
                      "You don't have permission to sync users for this organization."
                    )
                  }
                  sx={{
                    marginLeft: "5px",
                    color: "#008080",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "inline-block", minWidth: "8rem" }}>
                    <LoadingButton
                      text={"Sync Users"}
                      variant={"contained"}
                      backgroundColor={"#86BC25"}
                      color={"#fff"}
                      cursor={
                        isSyncStatusInProgress
                          ? "disabled"
                          : "pointer"
                      }
                      onClickFunc={openSyncModal}
                      loadingState={
                        isSyncInProgress ||
                        isTriggerOrgUserSyncLoading ||
                        isSyncStatusInProgress
                      }
                      loadingText={
                        isSyncInProgress ||
                          isSyncStatusInProgress
                          ? "In Progress"
                          : "Syncing..."
                      }
                      disabled={
                        isButtonDisabled ||
                        isTriggerOrgUserSyncLoading ||
                        !canUpdateOrganizationById
                        // ||
                        // rows.length === 0
                      }
                    />
                  </div>
                </Tooltip>
              )}
          </ThemeProvider>
          {/* Sync success or progress message */}
          {(isSyncInProgress ||
            isTriggerOrgUserSyncLoading ||
            isSuccessMessageVisible ||
            isSyncStatusInProgress) && (
              <Box
                sx={{
                  // mt: 1.5,
                  p: 0.8,
                  backgroundColor:
                    isSyncInProgress ||
                      isTriggerOrgUserSyncLoading ||
                      isSyncStatusInProgress
                      ? "#fff3cd"
                      : "#e8f5e9",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor:
                    isSyncInProgress ||
                      isTriggerOrgUserSyncLoading ||
                      isSyncStatusInProgress
                      ? "#ffeeba"
                      : "#b9f6ca",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {/* Show clock icon when sync is in progress and Show success tick icon when sync is successful */}

                {isSyncInProgress ||
                  isTriggerOrgUserSyncLoading ||
                  isSyncStatusInProgress ? (
                  <Zoom in={true} style={{ transitionDelay: "100ms" }}>
                    <AccessTimeIcon
                      sx={{
                        color: "#856404",
                      }}
                    />
                  </Zoom>
                ) : isSuccessMessageVisible ? (
                  <Zoom in={true} style={{ transitionDelay: "100ms" }}>
                    <TaskAltIcon
                      sx={{
                        color: "#2e7d32",
                      }}
                    />
                  </Zoom>
                ) : null}
                <Typography
                  variant="subtitle2"
                  sx={{
                    color:
                      isSyncInProgress ||
                        isTriggerOrgUserSyncLoading ||
                        isSyncStatusInProgress
                        ? "#856404"
                        : "#2e7d32",
                    fontWeight: "medium",
                  }}
                >
                  {isSyncInProgress ||
                    isTriggerOrgUserSyncLoading ||
                    isSyncStatusInProgress
                    ? "Sync In Progress, please wait.."
                    : "Success! Users Synced"}
                </Typography>
              </Box>
            )}
        </div>
        <hr style={{ backgroundColor: "#d0d7de", width: "100%" }}></hr>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "2rem",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#808080",
              lineHeight: "1.5",
              marginTop: "0.5rem",
              width: "60%",
            }}
          >
            {tabDescription}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {searchControls}
            <Tooltip title={noRows ? "Nothing to expand: the table has no rows" : "Open the table in a larger window"}>
              <span>
              <Button
                size="small"
                disabled={noRows}
                startIcon={<OpenInFullIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => setIsFullscreen(true)}
                sx={{ textTransform: "none", fontSize: "13px", color: "#555", whiteSpace: "nowrap" }}
              >
                Expand
              </Button>
              </span>
            </Tooltip>
            {!isFullscreen && columnsButton}
          </Box>
        </div>
        {filterChips && <div style={{ marginTop: "0.5rem" }}>{filterChips}</div>}
        {!isFullscreen && selectionBar}
      </div>
      {!isFullscreen && renderGrid("600px")}
      <Dialog
        open={isFullscreen}
        onClose={closeFullscreen}
        maxWidth={false}
        PaperProps={{ sx: { width: "calc(100vw - 48px)", height: "calc(100vh - 48px)", m: 0, borderRadius: "16px" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 600, fontSize: "18px" }}>
              {orgName || ""}
              <Typography variant="subtitle1">{viewType}</Typography>
            </div>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {isFullscreen && columnsButton}
              <Tooltip title="Exit full screen">
                <IconButton aria-label="Exit full screen" onClick={closeFullscreen}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {filterChips && <div style={{ marginTop: "0.5rem" }}>{filterChips}</div>}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            {selectionBar}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
              {isFullscreen && <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>{searchControls}</Box>}
              {onRecordsPerPageChange && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PageSizeSelect value={recordsPerPage} onChange={onRecordsPerPageChange} />
                </Box>
              )}
            </Box>
          </Box>
          {isFullscreen && renderGrid("calc(100vh - 220px)")}
        </Box>
      </Dialog>
    </div>
  );
};

export default ReusableDataGrid;
